import React, { useState, useRef } from "react";
import {
  Button,
  TextField,
  Link,
  Grid,
  Box,
  Typography,
  CircularProgress,
  InputAdornment,
  IconButton,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { authTextFieldStyle } from "core/theme";
import { apiClient } from "@/api/apiClient";
import { AuthErrorDialog } from "components";
import { Visibility, VisibilityOff } from "@mui/icons-material";

const SignUp = () => {
  const navigate = useNavigate();
  const formRef = useRef<HTMLFormElement>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [dialogContent, setDialogContent] = useState({
    title: "",
    errorMessage: "",
    showResetPassword: false,
    showResendVerification: false,
    email: "",
    customButton: undefined as
      | { text: string; onClick: () => void }
      | undefined,
    hideDefaultButton: false,
  });

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setLoading(true);
    setError(null);

    const formData = new FormData(event.currentTarget);
    const email = formData.get("email") as string;
    const password = formData.get("password") as string;
    const confirmPassword = formData.get("confirmPassword") as string;

    const requestData = {
      firstName: formData.get("firstName") as string,
      lastName: formData.get("lastName") as string,
      email,
      password,
      phone: (formData.get("phone") as string) || undefined,
      companyName: "Individual User",
      companyWebsite: undefined,
      message: undefined,
      isStudent: false,
      isFreelancer: false,
    };

    try {
      if (
        !requestData.firstName ||
        !requestData.lastName ||
        !requestData.email
      ) {
        setDialogContent({
          title: "Missing Information",
          errorMessage:
            "Please fill in all required fields: First Name, Last Name, and Email.",
          showResetPassword: false,
          showResendVerification: false,
          email: "",
          customButton: undefined,
          hideDefaultButton: false,
        });
        setOpenDialog(true);
        setLoading(false);
        return;
      }

      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(requestData.email)) {
        setDialogContent({
          title: "Invalid Email",
          errorMessage: "Please enter a valid email address.",
          showResetPassword: false,
          showResendVerification: false,
          email: "",
          customButton: undefined,
          hideDefaultButton: false,
        });
        setOpenDialog(true);
        setLoading(false);
        return;
      }

      if (!password) {
        setDialogContent({
          title: "Missing Password",
          errorMessage: "Please enter a password.",
          showResetPassword: false,
          showResendVerification: false,
          email: "",
          customButton: undefined,
          hideDefaultButton: false,
        });
        setOpenDialog(true);
        setLoading(false);
        return;
      }

      if (password.length < 8) {
        setDialogContent({
          title: "Password Too Short",
          errorMessage: "Password must be at least 8 characters long.",
          showResetPassword: false,
          showResendVerification: false,
          email: "",
          customButton: undefined,
          hideDefaultButton: false,
        });
        setOpenDialog(true);
        setLoading(false);
        return;
      }

      if (password !== confirmPassword) {
        setDialogContent({
          title: "Passwords Don't Match",
          errorMessage:
            "The passwords you entered don't match. Please try again.",
          showResetPassword: false,
          showResendVerification: false,
          email: "",
          customButton: undefined,
          hideDefaultButton: false,
        });
        setOpenDialog(true);
        setLoading(false);
        return;
      }

      await apiClient.public.signUpStakeholder(requestData);

      navigate("/login", {
        state: {
          successMessage: "Your account has been created successfully! You can now log in with your email and password.",
        },
      });
    } catch (err: any) {
      console.error("Error creating account:", err);

      if (err.message.includes("A user with this email already exists")) {
        setDialogContent({
          title: "Account Already Exists",
          errorMessage:
            "A user with this email already exists. Please use a different email or contact support if you need assistance.",
          showResetPassword: false,
          showResendVerification: false,
          email: "",
          customButton: {
            text: "Go to Login",
            onClick: () => navigate("/login"),
          },
          hideDefaultButton: false,
        });
      } else {
        setDialogContent({
          title: "Sign Up Failed",
          errorMessage:
            err.message ||
            "An error occurred while creating your account. Please try again later.",
          showResetPassword: false,
          showResendVerification: false,
          email: "",
          customButton: undefined,
          hideDefaultButton: false,
        });
      }

      setOpenDialog(true);
    } finally {
      setLoading(false);
    }
  };

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleClickShowConfirmPassword = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleNavigateToLogin = () => {
    navigate("/login");
  };

  return (
    <>
      <AuthErrorDialog
        open={openDialog}
        onClose={handleCloseDialog}
        title={dialogContent.title}
        errorMessage={dialogContent.errorMessage}
        showResetPassword={dialogContent.showResetPassword}
        showResendVerification={dialogContent.showResendVerification}
        email={dialogContent.email}
        customButton={dialogContent.customButton}
        hideDefaultButton={dialogContent.hideDefaultButton}
      />

      <Box>
        <Typography
          component="h1"
          variant="h5"
          sx={{ mb: 2, textAlign: "center", mt: 2, color: "black" }}
        >
          Sign Up
        </Typography>

        <Typography
          variant="body1"
          sx={{ mb: 3, textAlign: "center", color: "black" }}
        >
          Ship your MVP in as little as 14 days with our AI product development suite.
        </Typography>

        <Box
          component="form"
          ref={formRef}
          onSubmit={handleSubmit}
          noValidate
          sx={{ width: "100%" }}
        >
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                required
                fullWidth
                id="firstName"
                label="First Name"
                name="firstName"
                autoComplete="given-name"
                sx={{
                  ...authTextFieldStyle,
                  "& .MuiInputLabel-root": {
                    color: "rgba(0, 0, 0, 0.6)",
                    "&.Mui-focused": {
                      color: "black",
                    },
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                required
                fullWidth
                id="lastName"
                label="Last Name"
                name="lastName"
                autoComplete="family-name"
                sx={{
                  ...authTextFieldStyle,
                  "& .MuiInputLabel-root": {
                    color: "rgba(0, 0, 0, 0.6)",
                    "&.Mui-focused": {
                      color: "black",
                    },
                  },
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                required
                fullWidth
                id="email"
                label="Email Address"
                name="email"
                autoComplete="email"
                sx={{
                  ...authTextFieldStyle,
                  "& .MuiInputLabel-root": {
                    color: "rgba(0, 0, 0, 0.6)",
                    "&.Mui-focused": {
                      color: "black",
                    },
                  },
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                id="phone"
                label="Phone Number"
                name="phone"
                autoComplete="tel"
                sx={{
                  ...authTextFieldStyle,
                  "& .MuiInputLabel-root": {
                    color: "rgba(0, 0, 0, 0.6)",
                    "&.Mui-focused": {
                      color: "black",
                    },
                  },
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                required
                fullWidth
                name="password"
                label="Password"
                type={showPassword ? "text" : "password"}
                id="password"
                autoComplete="new-password"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={handleClickShowPassword}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  ...authTextFieldStyle,
                  "& .MuiInputLabel-root": {
                    color: "rgba(0, 0, 0, 0.6)",
                    "&.Mui-focused": {
                      color: "black",
                    },
                  },
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                required
                fullWidth
                name="confirmPassword"
                label="Confirm Password"
                type={showConfirmPassword ? "text" : "password"}
                id="confirmPassword"
                autoComplete="new-password"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={handleClickShowConfirmPassword}
                        edge="end"
                      >
                        {showConfirmPassword ? (
                          <VisibilityOff />
                        ) : (
                          <Visibility />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  ...authTextFieldStyle,
                  "& .MuiInputLabel-root": {
                    color: "rgba(0, 0, 0, 0.6)",
                    "&.Mui-focused": {
                      color: "black",
                    },
                  },
                }}
              />
            </Grid>
          </Grid>

          <Button
            fullWidth
            type="submit"
            disabled={loading}
            sx={{
              mt: 2,
              mb: 3,
              bgcolor: "black",
              color: "white",
              "&:hover": {
                bgcolor: "rgba(0, 0, 0, 0.8)",
              },
              position: "relative",
              minHeight: "42px",
              "& .MuiButton-startIcon": {
                marginRight: loading ? 0 : 1,
              },
            }}
          >
            <Box
              sx={{
                visibility: loading ? "hidden" : "visible",
                display: "flex",
                alignItems: "center",
              }}
            >
              Sign Up
            </Box>
            {loading && (
              <CircularProgress
                size={24}
                sx={{
                  color: "white",
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  marginTop: "-12px",
                  marginLeft: "-12px",
                }}
              />
            )}
          </Button>

          <Grid container justifyContent="center" sx={{ mt: 2 }}>
            <Grid item>
              <Link
                onClick={handleNavigateToLogin}
                variant="body2"
                sx={{ color: "black", cursor: "pointer" }}
              >
                Already have an account? Sign in
              </Link>
            </Grid>
          </Grid>
        </Box>
      </Box>
    </>
  );
};

export default SignUp;
